<template>
  <section v-if="items.length" class="fru" aria-labelledby="feature-release-updates">
    <div class="mx-auto max-w-6xl px-4 sm:px-6">
      <div class="fru-head">
        <h2 id="feature-release-updates">Recent updates</h2>
        <NuxtLink :to="localePath('/changelog')">All release notes <span aria-hidden="true">-&gt;</span></NuxtLink>
      </div>
      <ul class="fru-list">
        <li v-for="release in items" :key="release.slug">
          <NuxtLink :to="localePath(release.path)">
            <time :datetime="release.date">{{ release.dateLabel }}</time>
            <strong>{{ release.title }}</strong>
            <span>{{ release.summary }}</span>
          </NuxtLink>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup>
// Built from data/release-notes.json by modules/page-data.ts: only the releases that
// link to each path and the fields shown here, so a feature page doesn't download
// every release note.
import releaseLinks from '#build/page-data/release-links.mjs';
import { releaseLinkPath, releaseUpdatesLimit } from '~/utils/release-lastmod.mjs';

// Links a feature page back to the release notes that link to it, so every
// release entry and its feature page point at each other.
const props = defineProps({
  path: { type: String, required: true },
  limit: { type: Number, default: releaseUpdatesLimit },
});

const { localePath } = useWorkspaceLocale();
const items = computed(() => (releaseLinks[releaseLinkPath(props.path)] ?? []).slice(0, props.limit));
</script>

<style scoped>
.fru { border-top: 1px solid #e2e8f0; background: #ffffff; padding: 3.5rem 0; }
.dark .fru { border-color: #243247; background: #020617; }
.fru-head { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 0.75rem; }
.fru-head h2 { color: #0f172a; font-size: clamp(1.45rem, 2.6vw, 1.9rem); font-weight: 900; line-height: 1.15; }
.dark .fru-head h2 { color: #f8fafc; }
.fru-head a { color: #0e7490; font-size: 0.9rem; font-weight: 800; }
.dark .fru-head a { color: #67e8f9; }
.fru-list { display: grid; gap: 0.75rem; margin-top: 1.4rem; }
@media (min-width: 900px) { .fru-list { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.fru-list a {
  display: grid;
  gap: 0.4rem;
  height: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem 1.1rem;
  text-decoration: none;
  transition: border-color 140ms ease;
}
.fru-list a:hover { border-color: rgb(27 167 206 / 0.6); }
.dark .fru-list a { border-color: #243247; }
.fru-list time { color: #64748b; font-size: 0.75rem; font-weight: 800; }
.fru-list strong { color: #0f172a; font-size: 1rem; line-height: 1.35; }
.fru-list span { color: #475569; font-size: 0.88rem; line-height: 1.55; }
.dark .fru-list time { color: #94a3b8; }
.dark .fru-list strong { color: #f8fafc; }
.dark .fru-list span { color: #cbd5e1; }
</style>
